// CLARTEXT · Abschlussseite 2.0 · UI-V3
// Route: /abschluss-v2
//
// Struktur (verbindliches Betreiber-Mockup, Sep 2026):
//   Hero (unverändert) → Fork-Intro → 2 Entscheidungskarten (statt
//   Vergleichstabelle) → Risk-Note → Modul-Übersicht (10 Module + 2 Add-ons,
//   Trial-Banner) → finaler CTA-Block → Footer.
//
// Zwei-Türen-Logik UNVERÄNDERT: exakt zwei Optionen; Kauf über
// api.createOneTimeCheckout, Trial über /lp3. Der Kaufflow-Hook lebt auf
// Seitenebene, damit Karten und finaler CTA denselben Zustand teilen
// (Loading sperrt beide Kauf-Buttons, /api/catalog wird nur 1× geladen).
//
// CSS: streng gescoped unter `.abschluss-v2-root`. Font "Outfit" wird beim
// Seitenmount als <link>-Tag injiziert und beim Unmount entfernt.
import { useEffect, useRef } from "react";

import AbschlussHero, { AbschlussHeader } from "@/components/abschluss/AbschlussHero";
import EntscheidungsKarten, {
  AbschlussForkIntro,
} from "@/components/abschluss/EntscheidungsKarten";
import { useAbschlussKauffluss } from "@/components/abschluss/useAbschlussKauffluss";

import "./AbschlussseiteV2.css";

const OUTFIT_HREF =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap";
// Data-Attribut zum eindeutigen Wiedererkennen des von uns injizierten
// <link>-Tags (kein globaler Font-Impact, sauberes Aufräumen beim Unmount).
const OUTFIT_MARK = "abschluss-v2-outfit";

function ensureOutfitFontLink() {
  if (typeof document === "undefined") return null;
  const existing = document.querySelector(`link[data-clr-font="${OUTFIT_MARK}"]`);
  if (existing) return existing;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = OUTFIT_HREF;
  link.dataset.clrFont = OUTFIT_MARK;
  document.head.appendChild(link);
  return link;
}

export default function AbschlussseiteV2() {
  const rootRef = useRef(null);
  const kauffluss = useAbschlussKauffluss();

  // 1) Outfit-Font laden (nur für diese Seite) + beim Unmount entfernen.
  useEffect(() => {
    const link = ensureOutfitFontLink();
    return () => {
      if (link && link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, []);

  // 2) IntersectionObserver für die dezenten .reveal-Animationen.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      className="abschluss-v2-root"
      data-testid="abschluss-v2-page"
      lang="de"
      ref={rootRef}
    >
      <AbschlussHeader />
      <main>
        <AbschlussHero />
        <AbschlussForkIntro />
        <EntscheidungsKarten {...kauffluss} />
      </main>
      <footer
        className="foot"
        data-testid="abschluss-v2-footer"
        role="contentinfo"
      >
        CLartext · Arbeitsschutz-Dokumentation
        {/* ABO-RECHT-01, Betreiberfreigabe 24.10.2026: Rechtslinks wie Startseite (landing/index.html:286-289). */}
        <nav aria-label="Rechtliches" data-testid="abschluss-v2-legal-nav" className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
          <a href="/impressum" data-testid="abschluss-v2-legal-impressum">Impressum</a>
          <a href="/datenschutz" data-testid="abschluss-v2-legal-datenschutz">Datenschutz</a>
          <a href="/agb" data-testid="abschluss-v2-legal-agb">AGB</a>
          <a href="/avv" data-testid="abschluss-v2-legal-avv">Auftragsverarbeitung</a>
        </nav>
      </footer>
    </div>
  );
}
